import { ModalDialog } from '../../ModalDialog.ts'

/**
 * Round-trip back to the host game.
 *
 * When this page is opened with ?upload=<endpoint>&ticket=<scoped-token> — the game
 * deep-links here so a player can hand-rig their own generated character — the
 * exported GLB is POSTed straight back to that endpoint. The ticket is short-lived
 * and scoped to a single character, so carrying it in the URL is acceptable; it is
 * NOT the player's session token.
 *
 * No-ops when the params are absent, so the standalone app is unaffected.
 */
export class HandoffUploader {
  private static params (): { upload: string | null, ticket: string | null } {
    const query = new URLSearchParams(window.location.search)
    return { upload: query.get('upload'), ticket: query.get('ticket') }
  }

  public static is_active (): boolean {
    const { upload, ticket } = this.params()
    return upload !== null && upload !== '' && ticket !== null && ticket !== ''
  }

  /**
   * @param buffer exported GLB bytes
   * @param bone_naming the DownloadSettings bone naming structure ('mixamo' | 'default').
   *   Reported to the host so it can record which bone-name convention the file uses.
   */
  public static async upload (buffer: ArrayBuffer, bone_naming: string): Promise<void> {
    if (!this.is_active()) {
      return
    }

    const { upload, ticket } = this.params()
    const endpoint_base = upload as string
    const naming = bone_naming === 'mixamo' ? 'mixamo' : 'native'
    const endpoint = `${endpoint_base}${endpoint_base.includes('?') ? '&' : '?'}naming=${naming}`

    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'model/gltf-binary',
          Authorization: `Bearer ${ticket as string}`
        },
        body: buffer
      })

      if (!response.ok) {
        const detail = await response.text().catch(() => '')
        throw new Error(`${response.status} ${response.statusText} ${detail}`.trim())
      }

      new ModalDialog(
        'Sent back to the game',
        'Your rigged character was uploaded. You can close this tab and reload your character list in game.'
      ).show()
    } catch (err) {
      const message = (err instanceof Error) ? err.message : String(err)
      console.error('[forge handoff] upload failed', err)
      new ModalDialog(
        'Could not send it back',
        `${message}\n\nThe GLB still downloaded to your device, so nothing is lost — you can load it manually. (Upload links expire after 30 minutes.)`
      ).show()
    }
  }
}
